import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { 
  Upload, FileText, CheckCircle2, XCircle, Clock, Loader2,
  Eye, Trash2, FileCheck, AlertTriangle, ArrowRight, RefreshCw,
  ChevronRight, Download, Building2, Users, Euro, MapPin, Calendar,
  Sparkles, Check, X, FileWarning, ArrowRightLeft, UserMinus, UserPlus,
  Home
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Document type config
const DOC_TYPES = {
  statuts: { name: "Statuts", icon: FileText, color: "indigo" },
  kbis: { name: "Kbis", icon: FileCheck, color: "blue" },
  bilan: { name: "Bilan", icon: Euro, color: "green" },
  pv_ago: { name: "PV AGO", icon: FileText, color: "purple" },
  pv_age: { name: "PV AGE", icon: FileText, color: "orange" },
  cession: { name: "Cession", icon: ArrowRightLeft, color: "amber" },
  certificat_depot: { name: "Certificat de depot", icon: FileCheck, color: "teal" },
  bail_commercial: { name: "Bail Commercial", icon: Home, color: "rose" },
  autre: { name: "Document intelligent", icon: Sparkles, color: "pink" },
  inconnu: { name: "Inconnu", icon: FileWarning, color: "gray" }
};

// Helper to get doc type display name (handles "autre" with type_detecte)
const getDocTypeDisplayName = (doc) => {
  if (doc.document_type === "autre" && doc.type_detecte) {
    return doc.type_detecte;
  }
  return DOC_TYPES[doc.document_type]?.name || doc.document_type;
};

export default function ImportDocuments() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // doc à supprimer
  
  const fileInputRef = useRef(null);

  // Charge le PDF binaire d'un document pour la visualisation inline
  const openPdfViewer = async (docId, filename) => {
    setShowPdfViewer(true);
    setPdfLoading(true);
    setPdfUrl(null);
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/pdf/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const b64 = res.data.content_b64;
      // Décode base64 → Blob → URL
      const byteChars = atob(b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error("Impossible de charger le PDF");
      setShowPdfViewer(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setShowPdfViewer(false);
  };

  // Fetch document history
  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data.documents || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Handle file upload
  const handleUpload = async (file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Seuls les fichiers PDF sont acceptes");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/api/corporate/ocr/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        toast.success(`Document uploade: ${res.data.message}`);
        fetchHistory();
        
        // Auto-select the new document
        setSelectedDoc({
          id: res.data.document_id,
          filename: res.data.filename,
          document_type: res.data.document_type,
          type_detecte: res.data.type_detecte || "",
          classification_confidence: res.data.classification_confidence,
          status: "uploaded"
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  // Handle drag and drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  // Run extraction
  const runExtraction = async (docId) => {
    setExtracting(true);
    setExtractionResult(null);
    
    try {
      const res = await axios.post(`${API}/api/corporate/ocr/extract/${docId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setExtractionResult(res.data);
        setShowPreview(true);
        toast.success("Extraction terminee avec succes");
        fetchHistory();
        
        // Update selectedDoc with new status and data
        setSelectedDoc(prev => ({
          ...prev,
          status: "completed",
          applied: false,
          differences: res.data.differences || [],
          extracted_data: res.data.extracted_data,
          profile_data: res.data.profile_data
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'extraction");
    } finally {
      setExtracting(false);
    }
  };

  // State for validation warnings
  const [validationWarning, setValidationWarning] = useState(null);
  const [forceApplyMode, setForceApplyMode] = useState(false);

  // Apply to profile with smart validation
  const applyToProfile = async (forceApply = false) => {
    if (!selectedDoc) return;
    
    setLoading(true);
    setValidationWarning(null);
    
    try {
      const res = await axios.post(`${API}/api/corporate/ocr/validate/${selectedDoc.id}`, {
        document_id: selectedDoc.id,
        apply_to_profile: true,
        force_apply: forceApply
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowPreview(false);
        setSelectedDoc(null);
        setExtractionResult(null);
        setForceApplyMode(false);
        fetchHistory();
      } else if (res.data.action === "blocked" || res.data.action === "confirmation_required") {
        // Show validation warning
        setValidationWarning(res.data);
        setForceApplyMode(true);
        toast.warning("Verification requise avant application");
      }
    } catch (error) {
      console.error("Apply to profile error:", error);
      let errorMsg = "Erreur lors de l'application";
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (typeof error.message === 'string') {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Delete document (Import + Coffre-Fort en cascade)
  const deleteDocument = async (docId) => {
    try {
      const res = await axios.delete(`${API}/api/corporate/ocr/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cascade = res.data?.cascade || {};
      const extras = [];
      if (cascade.assemblees_removed) extras.push(`${cascade.assemblees_removed} assemblée(s)`);
      if (cascade.exercices_removed) extras.push(`${cascade.exercices_removed} exercice(s)`);
      if (cascade.generated_removed) extras.push(`${cascade.generated_removed} document(s) généré(s)`);
      toast.success(
        extras.length
          ? `Document supprimé (Coffre-Fort + ${extras.join(", ")})`
          : "Document supprimé du Coffre-Fort"
      );
      fetchHistory();
      if (selectedDoc?.id === docId) {
        setSelectedDoc(null);
        setExtractionResult(null);
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "uploaded":
        return <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" /> En attente</Badge>;
      case "processing":
        return <Badge className="gap-1 bg-blue-500"><Loader2 className="w-3 h-3 animate-spin" /> Extraction...</Badge>;
      case "completed":
        return <Badge className="gap-1 bg-green-500"><CheckCircle2 className="w-3 h-3" /> Termine</Badge>;
      case "failed":
        return <Badge className="gap-1 bg-red-500"><XCircle className="w-3 h-3" /> Erreur</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDocTypeConfig = (type) => DOC_TYPES[type] || DOC_TYPES.inconnu;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Import Intelligent</h2>
          <p className="text-gray-500">Importez vos documents juridiques, l'IA extrait automatiquement les donnees</p>
        </div>
        <Button variant="outline" onClick={fetchHistory} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Zone + History */}
        <div className="lg:col-span-1 space-y-4">
          {/* Upload Zone */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Nouveau document</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive 
                    ? "border-indigo-500 bg-indigo-50" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {uploading ? (
                  <div className="space-y-3">
                    <Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin" />
                    <p className="text-sm text-gray-600">Upload en cours...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm font-medium text-gray-700">
                      Glissez votre PDF ici
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ou cliquez pour parcourir
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files[0])}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Parcourir
                    </Button>
                  </>
                )}
              </div>
              
              <div className="mt-4 text-xs text-gray-500">
                <p className="font-medium mb-1">Documents supportes :</p>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2">
                    <FileText className="w-3 h-3 text-indigo-500" /> Statuts
                  </li>
                  <li className="flex items-center gap-2">
                    <FileCheck className="w-3 h-3 text-blue-500" /> Kbis
                  </li>
                  <li className="flex items-center gap-2">
                    <Euro className="w-3 h-3 text-green-500" /> Bilan / Liasse fiscale
                  </li>
                  <li className="flex items-center gap-2">
                    <FileText className="w-3 h-3 text-purple-500" /> PV AGO / AGE
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRightLeft className="w-3 h-3 text-amber-500" /> Acte de cession
                  </li>
                  <li className="flex items-center gap-2">
                    <Home className="w-3 h-3 text-rose-500" /> Bail commercial
                  </li>
                  <li className="flex items-center gap-2">
                    <FileCheck className="w-3 h-3 text-teal-500" /> Certificat de dépôt
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Document History */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Documents importes</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[300px]">
                {documents.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">
                    Aucun document importe
                  </p>
                ) : (
                  <div className="divide-y">
                    {documents.map((doc) => {
                      const typeConfig = getDocTypeConfig(doc.document_type);
                      const TypeIcon = typeConfig.icon;
                      
                      return (
                        <div
                          key={doc.id}
                          onClick={() => {
                            setSelectedDoc(doc);
                            if (doc.status === "completed") {
                              // Load preview
                              axios.get(`${API}/api/corporate/ocr/preview/${doc.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              }).then(res => {
                                setExtractionResult(res.data);
                                setShowPreview(true);
                              });
                            } else {
                              setExtractionResult(null);
                              setShowPreview(false);
                            }
                          }}
                          className={`p-3 cursor-pointer transition-colors ${
                            selectedDoc?.id === doc.id 
                              ? "bg-indigo-50" 
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg bg-${typeConfig.color}-100`}>
                              <TypeIcon className={`w-4 h-4 text-${typeConfig.color}-600`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {doc.filename}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {typeConfig.name}
                                </Badge>
                                {getStatusBadge(doc.status)}
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(doc.created_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(doc);
                              }}
                              data-testid={`delete-doc-${doc.id}`}
                              title="Supprimer (Import + Coffre-Fort)"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Extraction Panel */}
        <div className="lg:col-span-2">
          <Card className="h-full">
            {!selectedDoc ? (
              <div className="h-full flex items-center justify-center p-12">
                <div className="text-center">
                  <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900">Import Intelligent</h3>
                  <p className="text-gray-500 mt-2 max-w-sm">
                    Selectionnez un document dans la liste ou importez-en un nouveau pour commencer l'extraction automatique.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{selectedDoc.filename}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        Type: {getDocTypeDisplayName(selectedDoc)}
                        {selectedDoc.document_type === "autre" && (
                          <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Analyse intelligente
                          </Badge>
                        )}
                        {selectedDoc.classification_confidence && (
                          <span className="text-xs text-gray-400">
                            (confiance: {Math.round(selectedDoc.classification_confidence * 100)}%)
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPdfViewer(selectedDoc.id, selectedDoc.filename)}
                        className="gap-2"
                        data-testid="view-pdf-btn"
                      >
                        <Eye className="w-4 h-4" />
                        Voir PDF
                      </Button>
                      {selectedDoc.status === "uploaded" && (
                        <Button
                          onClick={() => runExtraction(selectedDoc.id)}
                          disabled={extracting}
                          className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                        >
                          {extracting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Extraction...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Lancer l'extraction
                            </>
                          )}
                        </Button>
                      )}
                      
                      {selectedDoc.status === "completed" && !selectedDoc.applied && (
                        <Button
                          onClick={() => applyToProfile(false)}
                          disabled={loading}
                          className="gap-2 bg-green-600 hover:bg-green-700"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          Appliquer au profil
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-0">
                  {extracting && (
                    <div className="p-12 text-center">
                      <Loader2 className="w-12 h-12 mx-auto text-indigo-500 animate-spin mb-4" />
                      <h3 className="font-medium text-gray-900">Extraction en cours...</h3>
                      <p className="text-sm text-gray-500 mt-2">
                        L'IA analyse votre document et extrait les informations.
                        <br />Cela peut prendre quelques secondes.
                      </p>
                    </div>
                  )}

                  {!extracting && extractionResult && showPreview && (
                    <ScrollArea className="h-[500px] p-6">
                      {/* Validation Warning */}
                      {validationWarning && (
                        <div className="mb-4 p-4 rounded-lg border bg-amber-50 border-amber-300">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-500" />
                            <div className="flex-1">
                              <h4 className="font-medium text-amber-800">
                                Verification recommandee
                              </h4>
                              <p className="text-sm mt-1 text-gray-700">{validationWarning.message}</p>
                              
                              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                <CheckCircle2 className="inline w-3 h-3 mr-1" />
                                Le document est deja stocke et accessible pour le chatbot IA et les generateurs.
                              </div>
                              
                              {/* Show conflicts */}
                              {validationWarning.validation?.conflicts?.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <p className="text-xs font-medium text-gray-600">Changements sur le profil officiel:</p>
                                  {validationWarning.validation.conflicts.map((c, idx) => (
                                    <div key={idx} className={`text-xs p-2 rounded ${
                                      c.severity === "high" ? "bg-red-100" : "bg-amber-100"
                                    }`}>
                                      <span className="font-medium">{c.field}:</span>{" "}
                                      <span className="text-gray-600">{c.current}</span>{" "}
                                      <ArrowRight className="inline w-3 h-3" />{" "}
                                      <span className={c.severity === "high" ? "text-red-700 font-medium" : "text-amber-700"}>
                                        {c.new}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Action buttons */}
                              <div className="mt-3 flex gap-2">
                                <Button
                                  onClick={() => applyToProfile(true)}
                                  disabled={loading}
                                  className="bg-amber-600 hover:bg-amber-700 text-white"
                                  size="sm"
                                >
                                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                                  Confirmer et mettre a jour le profil
                                </Button>
                                <Button
                                  onClick={() => {
                                    setValidationWarning(null);
                                    setForceApplyMode(false);
                                    toast.info("Document conserve sans modification du profil");
                                  }}
                                  variant="outline"
                                  size="sm"
                                >
                                  Garder tel quel
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <ExtractionPreview 
                        data={extractionResult} 
                        docType={selectedDoc.document_type}
                      />
                    </ScrollArea>
                  )}

                  {!extracting && !showPreview && selectedDoc.status === "uploaded" && (
                    <div className="p-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <h3 className="font-medium text-gray-900">Document pret pour l'extraction</h3>
                      <p className="text-sm text-gray-500 mt-2">
                        Cliquez sur "Lancer l'extraction" pour demarrer l'analyse OCR intelligente.
                      </p>
                    </div>
                  )}

                  {selectedDoc.status === "failed" && (
                    <div className="p-12 text-center">
                      <XCircle className="w-12 h-12 mx-auto text-red-400 mb-4" />
                      <h3 className="font-medium text-gray-900">Extraction echouee</h3>
                      <p className="text-sm text-gray-500 mt-2">
                        Une erreur s'est produite lors de l'extraction.
                        <br />Essayez de reimporter le document.
                      </p>
                    </div>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Confirmation suppression (cascade Import + Coffre-Fort) */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{deleteConfirm?.filename}</span> sera supprimé
              définitivement de la page Import <strong>ET du Coffre-Fort</strong>. Les données
              archivées liées (exercices comptables, assemblées, documents générés) seront également
              nettoyées en cascade. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && deleteDocument(deleteConfirm.id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
          onClick={closePdfViewer}
          data-testid="pdf-viewer-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-indigo-600 shrink-0" />
                <span className="font-medium text-slate-900 truncate">
                  {selectedDoc?.filename}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {pdfUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    data-testid="pdf-download-btn"
                  >
                    <a href={pdfUrl} download={selectedDoc?.filename}>
                      <Download className="w-4 h-4 mr-1" />
                      Télécharger
                    </a>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closePdfViewer}
                  data-testid="pdf-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100 overflow-hidden">
              {pdfLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  title={selectedDoc?.filename}
                  className="w-full h-full border-0"
                  data-testid="pdf-iframe"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-500">
                  Erreur de chargement du PDF
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Extraction Preview Component
function ExtractionPreview({ data, docType }) {
  const extracted = data?.extracted_data;
  const confidence = extracted?.confidence_globale || 0;
  const differences = data?.differences || [];

  // Check what type of data we have
  const hasProfileData = !!data?.profile_data;
  const hasExerciceData = !!data?.exercice_data;
  const hasAssembleeData = !!data?.assemblee_data;
  const hasMouvementData = !!data?.mouvement_data;
  const hasRawExtracted = !!extracted?.donnees || !!extracted?.societe;
  
  // For "certificat_depot", "bail_commercial", "autre" and similar types - they have data in extracted_data directly
  const isGenericExtraction = ["certificat_depot", "bail_commercial", "autre"].includes(docType);

  if (!hasProfileData && !hasExerciceData && !hasAssembleeData && !hasMouvementData && !isGenericExtraction && !hasRawExtracted) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
        <p className="text-gray-600">Aucune donnee extraite</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confidence Score */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-700">Confiance globale</span>
            <span className="text-sm font-bold text-indigo-600">
              {Math.round(confidence * 100)}%
            </span>
          </div>
          <Progress value={confidence * 100} className="h-2" />
        </div>
        {confidence >= 0.8 && (
          <Badge className="bg-green-500 gap-1">
            <CheckCircle2 className="w-3 h-3" /> Fiable
          </Badge>
        )}
        {confidence >= 0.5 && confidence < 0.8 && (
          <Badge className="bg-amber-500 gap-1">
            <AlertTriangle className="w-3 h-3" /> A verifier
          </Badge>
        )}
        {confidence < 0.5 && (
          <Badge className="bg-red-500 gap-1">
            <XCircle className="w-3 h-3" /> Peu fiable
          </Badge>
        )}
      </div>

      {/* Differences Alert */}
      {differences.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-800">
              {differences.length} difference(s) avec les donnees existantes
            </span>
          </div>
          <div className="space-y-2">
            {differences.map((diff, idx) => (
              <div key={idx} className="text-sm flex items-center gap-2">
                <span className="text-gray-600">{diff.label}:</span>
                <span className="text-red-600 line-through">{formatValue(diff.old_value)}</span>
                <ArrowRight className="w-3 h-3 text-gray-400" />
                <span className="text-green-600 font-medium">{formatValue(diff.new_value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Data (Statuts/Kbis) */}
      {hasProfileData && <ProfilePreview profile={data.profile_data} isKbis={docType === "kbis"} />}

      {/* Profile Updates Alert (AGE) */}
      {data?.profile_updates && Object.keys(data.profile_updates).length > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">
              Modifications a appliquer au profil societe
            </span>
          </div>
          <div className="space-y-1">
            {data.profile_updates.capital_social && (
              <p className="text-sm text-blue-700">• Capital: {data.profile_updates.capital_social.toLocaleString()} EUR</p>
            )}
            {data.profile_updates.nombre_parts && (
              <p className="text-sm text-blue-700">• Nombre de parts: {data.profile_updates.nombre_parts}</p>
            )}
            {data.profile_updates.siege_social && (
              <p className="text-sm text-blue-700">• Siege: {data.profile_updates.siege_social.adresse}, {data.profile_updates.siege_social.code_postal} {data.profile_updates.siege_social.ville}</p>
            )}
            {data.profile_updates.raison_sociale && (
              <p className="text-sm text-blue-700">• Denomination: {data.profile_updates.raison_sociale}</p>
            )}
            {data.profile_updates.forme_juridique && (
              <p className="text-sm text-blue-700">• Forme juridique: {data.profile_updates.forme_juridique}</p>
            )}
          </div>
        </div>
      )}

      {/* Exercice Data (Bilan) */}
      {hasExerciceData && <ExercicePreview exercice={data.exercice_data} />}

      {/* Assemblee Data (PV AGO/AGE) */}
      {hasAssembleeData && <AssembleePreview assemblee={data.assemblee_data} docType={docType} />}

      {/* Mouvement Data (Cession) */}
      {data?.mouvement_data && <CessionPreview mouvement={data.mouvement_data} />}
      
      {/* Generic Extraction Data (certificat_depot, autre) */}
      {isGenericExtraction && extracted && (
        <GenericExtractionPreview extracted={extracted} docType={docType} />
      )}
    </div>
  );
}

// Generic Extraction Preview (for certificat_depot, autre, etc.)
function GenericExtractionPreview({ extracted, docType }) {
  const donnees = extracted?.donnees || {};
  const societe = donnees?.societe || extracted?.societe || {};
  
  // For "autre" type, show the detected type
  const detectedType = extracted?.document_type_detecte;
  const description = extracted?.description_document;
  const resume = extracted?.resume;
  const elementsCles = extracted?.elements_cles || [];
  const personnes = extracted?.personnes_mentionnees || [];
  const dates = extracted?.dates_importantes || [];
  const montants = extracted?.montants || [];
  const donneesPrincipales = extracted?.donnees_principales || {};
  
  // Helper to get value from {valeur, confidence} or direct value
  const getVal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'object' && 'valeur' in obj) return obj.valeur;
    return obj;
  };

  return (
    <div className="space-y-4">
      {/* Detected Type for "autre" */}
      {docType === "autre" && detectedType && (
        <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-pink-600" />
            <span className="font-medium text-pink-800">
              Document detecte: {detectedType}
            </span>
          </div>
          {description && (
            <p className="text-sm text-pink-700">{description}</p>
          )}
        </div>
      )}
      
      {/* Resume */}
      {resume && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              Resume
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700">{resume}</p>
          </CardContent>
        </Card>
      )}
      
      {/* Elements Cles */}
      {elementsCles.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              Points cles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1">
              {elementsCles.map((el, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">•</span>
                  <span className="text-gray-700">{el}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Societe Info */}
      {(getVal(societe.denomination) || getVal(societe.siren)) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-500" />
              Societe concernee
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow label="Denomination" value={getVal(societe.denomination)} />
            <DataRow label="SIREN" value={getVal(societe.siren)} highlight />
            <DataRow label="Forme juridique" value={getVal(societe.forme_juridique)} />
            <DataRow label="RCS" value={getVal(societe.rcs)} />
          </CardContent>
        </Card>
      )}

      {/* Certificat Depot specific */}
      {docType === "certificat_depot" && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-teal-500" />
                Informations du depot
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <DataRow label="Greffe" value={getVal(donnees.greffe)} />
              <DataRow label="Tribunal" value={getVal(donnees.tribunal)} />
              <DataRow label="Numero de depot" value={getVal(donnees.numero_depot)} highlight />
              <DataRow label="Date de depot" value={getVal(donnees.date_depot)} />
              <DataRow label="Reference" value={getVal(donnees.reference_interne)} />
            </CardContent>
          </Card>
          
          {/* Actes Deposes */}
          {donnees.actes_deposes && donnees.actes_deposes.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-500" />
                  Actes deposes ({donnees.actes_deposes.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {donnees.actes_deposes.map((acte, idx) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                      <div className="font-medium text-gray-900">{acte.type_acte || "Acte"}</div>
                      {acte.date_acte && <div className="text-gray-500 text-xs">Date: {acte.date_acte}</div>}
                      {acte.description && <div className="text-gray-600 mt-1">{acte.description}</div>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Donnees Principales (for "autre" type) */}
      {Object.keys(donneesPrincipales).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              Donnees extraites
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {Object.entries(donneesPrincipales).map(([key, value]) => (
              <DataRow 
                key={key} 
                label={key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())} 
                value={typeof value === 'object' ? JSON.stringify(value) : value} 
              />
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Personnes Mentionnees */}
      {personnes.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-500" />
              Personnes mentionnees ({personnes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {personnes.map((p, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">{p.role || p.qualite || "Personne"}</Badge>
                  <span className="font-medium">{p.prenom} {p.nom}</span>
                  {p.fonction && <span className="text-gray-500">({p.fonction})</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Dates Importantes */}
      {dates.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-red-500" />
              Dates importantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dates.map((d, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{d.type || d.description}</span>
                  <Badge variant="secondary">{d.date}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Montants */}
      {montants.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Euro className="w-4 h-4 text-green-500" />
              Montants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {montants.map((m, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{m.type || m.description}</span>
                  <span className="font-bold text-green-600">
                    {typeof m.montant === 'number' ? m.montant.toLocaleString() : m.montant} {m.devise || 'EUR'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Profile Preview (Statuts)
function ProfilePreview({ profile, isKbis = false }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-500" />
              Identite
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow label="Raison sociale" value={profile.raison_sociale} />
            <DataRow label="Forme juridique" value={profile.forme_juridique} />
            <DataRow label="Sigle" value={profile.sigle} />
            {!isKbis && <DataRow label="Enseigne" value={profile.enseigne} />}
            {isKbis && (
              <>
                <Separator className="my-2" />
                <DataRow label="SIREN" value={profile.siren} highlight />
                <DataRow label="SIRET" value={profile.siret} />
                <DataRow label="RCS" value={profile.rcs?.numero ? `${profile.rcs.numero} (${profile.rcs.ville})` : null} />
                <DataRow label="TVA Intra." value={profile.tva_intracommunautaire} />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-500" />
              Siege Social
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow label="Adresse" value={profile.siege_social?.adresse} />
            <DataRow label="Code postal" value={profile.siege_social?.code_postal} />
            <DataRow label="Ville" value={profile.siege_social?.ville} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Euro className="w-4 h-4 text-green-500" />
              Capital
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow 
              label="Capital social" 
              value={profile.capital_social ? `${profile.capital_social.toLocaleString()} EUR` : null} 
            />
            {!isKbis && (
              <>
                <DataRow label="Nombre de titres" value={profile.nombre_parts} />
                <DataRow label="Valeur nominale" value={profile.valeur_nominale ? `${profile.valeur_nominale} EUR` : null} />
                <DataRow label="Type" value={profile.type_titres} />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-500" />
              {isKbis ? "Activite" : "Dates"}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow label={isKbis ? "Date immatriculation" : "Date de creation"} value={profile.date_creation} />
            {!isKbis && (
              <>
                <DataRow label="Duree" value={profile.duree_societe} />
                <DataRow label="Cloture exercice" value={profile.date_cloture_exercice} />
              </>
            )}
            {isKbis && (
              <>
                <DataRow label="Code APE/NAF" value={profile.code_ape || profile.code_naf} />
                <DataRow label="Activite principale" value={profile.activite_principale} />
                <DataRow label="Greffe" value={profile.greffe} />
                <DataRow label="Date extrait" value={profile.date_extrait_kbis} />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {!isKbis && profile.associes?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-500" />
              Associes / Actionnaires ({profile.associes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {profile.associes.map((associe, idx) => (
                <div key={idx} className="py-2 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {associe.type === "personne_morale" 
                        ? associe.denomination 
                        : `${associe.civilite || ''} ${associe.prenom || ''} ${associe.nom || ''}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {associe.type === "personne_morale" ? "Personne morale" : "Personne physique"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{associe.nombre_parts} {profile.type_titres || "parts"}</p>
                    <p className="text-xs text-indigo-600">{associe.pourcentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {profile.dirigeants?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" />
              Dirigeant(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {profile.dirigeants.map((dir, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm">{dir.civilite} {dir.prenom} {dir.nom}</span>
                  {dir.date_nomination && (
                    <p className="text-xs text-gray-500">Nommé le {dir.date_nomination}</p>
                  )}
                </div>
                <Badge variant="outline">{dir.fonction}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isKbis && profile.observations && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Observations</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">{profile.observations}</p>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// Exercice Preview (Bilan)
function ExercicePreview({ exercice }) {
  const cr = exercice.compte_resultat || {};
  const bilan = exercice.bilan || {};

  return (
    <>
      {/* Header with year */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Euro className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-900">
              Exercice {exercice.annee || "N/A"}
            </h3>
            <p className="text-sm text-green-700">
              {exercice.periode?.debut} - {exercice.periode?.fin}
              {exercice.periode?.duree_mois && exercice.periode.duree_mois !== 12 && 
                ` (${exercice.periode.duree_mois} mois)`}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Compte de Resultat */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Compte de Resultat</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow label="Chiffre d'affaires" value={formatCurrency(cr.chiffre_affaires)} highlight />
            <DataRow label="Resultat d'exploitation" value={formatCurrency(cr.resultat_exploitation)} />
            <DataRow label="Resultat financier" value={formatCurrency(cr.resultat_financier)} />
            <DataRow label="Resultat exceptionnel" value={formatCurrency(cr.resultat_exceptionnel)} />
            <Separator className="my-2" />
            <DataRow label="Resultat net" value={formatCurrency(cr.resultat_net)} highlight />
          </CardContent>
        </Card>

        {/* Bilan Passif */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Capitaux Propres</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow label="Capital social" value={formatCurrency(bilan.capital_social)} />
            <DataRow label="Reserve legale" value={formatCurrency(bilan.reserve_legale)} />
            <DataRow label="Autres reserves" value={formatCurrency(bilan.autres_reserves)} />
            <DataRow label="Report a nouveau" value={formatCurrency(bilan.report_a_nouveau)} />
            <DataRow label="Resultat exercice" value={formatCurrency(bilan.resultat_exercice)} />
            <Separator className="my-2" />
            <DataRow label="Capitaux propres" value={formatCurrency(bilan.capitaux_propres)} highlight />
          </CardContent>
        </Card>
      </div>

      {exercice.effectif_moyen > 0 && (
        <Card>
          <CardContent className="py-3">
            <DataRow label="Effectif moyen" value={exercice.effectif_moyen + " salarie(s)"} />
          </CardContent>
        </Card>
      )}
    </>
  );
}

// Assemblee Preview (PV AGO/AGE)
function AssembleePreview({ assemblee, docType }) {
  const isAGO = assemblee.type === "AGO";

  return (
    <>
      {/* Header */}
      <div className={`p-4 ${isAGO ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'} border rounded-lg`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 ${isAGO ? 'bg-purple-100' : 'bg-orange-100'} rounded-lg`}>
            <FileText className={`w-6 h-6 ${isAGO ? 'text-purple-600' : 'text-orange-600'}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${isAGO ? 'text-purple-900' : 'text-orange-900'}`}>
              {assemblee.type} - {assemblee.date_assemblee || "Date non detectee"}
              {assemblee.heure_assemblee && ` a ${assemblee.heure_assemblee}`}
            </h3>
            {assemblee.lieu && (
              <p className="text-xs text-gray-500">{assemblee.lieu}</p>
            )}
            {isAGO && assemblee.exercice_concerne && (
              <p className={`text-sm ${isAGO ? 'text-purple-700' : 'text-orange-700'}`}>
                Exercice concerne: {assemblee.exercice_concerne}
              </p>
            )}
            {!isAGO && assemblee.type_decision && (
              <p className="text-sm text-orange-700">
                Type: {assemblee.type_decision.replace(/_/g, ' ')}
              </p>
            )}
            {!isAGO && assemblee.objet_principal && (
              <p className="text-sm text-orange-600 font-medium mt-1">
                {assemblee.objet_principal}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* AGO specific */}
      {isAGO && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Resultat et Affectation</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <DataRow label="Resultat net" value={formatCurrency(assemblee.resultat_net)} highlight />
              {assemblee.report_anterieur > 0 && (
                <DataRow label="Report anterieur" value={formatCurrency(assemblee.report_anterieur)} />
              )}
              {assemblee.benefice_distribuable > 0 && (
                <DataRow label="Benefice distribuable" value={formatCurrency(assemblee.benefice_distribuable)} />
              )}
              <Separator className="my-2" />
              <DataRow label="Reserve legale" value={formatCurrency(assemblee.affectation?.reserve_legale)} />
              {assemblee.affectation?.reserve_statutaire > 0 && (
                <DataRow label="Reserve statutaire" value={formatCurrency(assemblee.affectation?.reserve_statutaire)} />
              )}
              <DataRow label="Dividendes" value={formatCurrency(assemblee.affectation?.dividendes)} />
              {assemblee.affectation?.dividende_par_part > 0 && (
                <DataRow label="Dividende/part" value={formatCurrency(assemblee.affectation?.dividende_par_part)} />
              )}
              <DataRow label="Report a nouveau" value={formatCurrency(assemblee.affectation?.report_a_nouveau)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Decisions</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-center gap-2">
                {assemblee.quitus_accorde ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <span>Quitus {assemblee.dirigeant_quitus ? `a ${assemblee.dirigeant_quitus}` : "au dirigeant"}</span>
              </div>
              <div className="flex items-center gap-2">
                {assemblee.quorum_atteint ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500" />
                )}
                <span>Quorum atteint ({assemblee.pourcentage_present || 0}%)</span>
              </div>
              {/* Conventions reglementees */}
              {assemblee.conventions_reglementees?.existantes && (
                <div className="flex items-center gap-2 pt-2">
                  {assemblee.conventions_reglementees.approuvees ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  )}
                  <span>Conventions reglementees {assemblee.conventions_reglementees.approuvees ? "approuvees" : "non approuvees"}</span>
                </div>
              )}
              {/* Renouvellement mandat */}
              {assemblee.renouvellement_mandat?.concerne && (
                <div className="flex items-center gap-2 pt-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-500" />
                  <span>Mandat renouvele ({assemblee.renouvellement_mandat.dirigeant})</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* AGO Resolutions if any */}
      {isAGO && assemblee.resolutions?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Resolutions adoptees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {assemblee.resolutions.map((res, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                  {res.adoptee !== false ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium">{res.titre || `Resolution ${res.numero}`}</p>
                    {res.description && (
                      <p className="text-xs text-gray-500">{res.description}</p>
                    )}
                  </div>
                  {(res.votes_pour > 0 || res.votes_contre > 0) && (
                    <span className="text-xs text-gray-400">
                      {res.votes_pour} pour / {res.votes_contre} contre
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AGE Modifications */}
      {!isAGO && assemblee.modifications && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Modifications statutaires</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-3">
            {assemblee.modifications.capital?.modifie && (
              <div className="p-2 bg-green-50 rounded">
                <p className="font-medium text-green-800">Modification du capital</p>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <DataRow label="Avant" value={formatCurrency(assemblee.modifications.capital.montant_avant)} />
                  <DataRow label="Apres" value={formatCurrency(assemblee.modifications.capital.montant_apres)} highlight />
                </div>
                {assemblee.modifications.capital.modalites && (
                  <p className="text-xs text-gray-500 mt-1">{assemblee.modifications.capital.modalites}</p>
                )}
              </div>
            )}
            {assemblee.modifications.siege_social?.modifie && (
              <div className="p-2 bg-blue-50 rounded">
                <p className="font-medium text-blue-800">Transfert de siege</p>
                <p className="text-xs text-gray-600">Avant: {assemblee.modifications.siege_social.adresse_avant}</p>
                <p className="text-xs text-blue-700 font-medium">
                  Apres: {assemblee.modifications.siege_social.adresse_apres}, 
                  {assemblee.modifications.siege_social.code_postal_apres} {assemblee.modifications.siege_social.ville_apres}
                </p>
              </div>
            )}
            {assemblee.modifications.denomination?.modifie && (
              <div className="p-2 bg-purple-50 rounded">
                <p className="font-medium text-purple-800">Changement de denomination</p>
                <p className="text-xs text-gray-600">Avant: {assemblee.modifications.denomination.avant}</p>
                <p className="text-xs text-purple-700 font-medium">Apres: {assemblee.modifications.denomination.apres}</p>
              </div>
            )}
            {assemblee.modifications.dirigeant?.modifie && (
              <div className="p-2 bg-indigo-50 rounded">
                <p className="font-medium text-indigo-800">Modification de direction</p>
                {assemblee.modifications.dirigeant.nomination?.nom && (
                  <p className="text-xs text-green-600">
                    Nomination: {assemblee.modifications.dirigeant.nomination.prenom} {assemblee.modifications.dirigeant.nomination.nom} ({assemblee.modifications.dirigeant.nomination.fonction})
                  </p>
                )}
                {assemblee.modifications.dirigeant.revocation?.nom && (
                  <p className="text-xs text-red-600">
                    Revocation: {assemblee.modifications.dirigeant.revocation.prenom} {assemblee.modifications.dirigeant.revocation.nom}
                  </p>
                )}
              </div>
            )}
            {assemblee.modifications.transformation?.modifie && (
              <div className="p-2 bg-orange-50 rounded">
                <p className="font-medium text-orange-800">Transformation</p>
                <p className="text-xs text-gray-600">Avant: {assemblee.modifications.transformation.forme_avant}</p>
                <p className="text-xs text-orange-700 font-medium">Apres: {assemblee.modifications.transformation.forme_apres}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AGE Resolutions */}
      {!isAGO && assemblee.resolutions?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Resolutions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {assemblee.resolutions.map((res, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                  {res.adoptee ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{res.titre || `Resolution ${res.numero}`}</p>
                    {res.description && (
                      <p className="text-xs text-gray-500">{res.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants */}
      {assemblee.participants?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4" />
              Participants ({assemblee.participants.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {assemblee.participants.map((p, idx) => (
                <div key={idx} className="py-2 flex items-center justify-between">
                  <span className="text-sm">{p.prenom} {p.nom}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{p.parts} parts</span>
                    <Badge variant={p.presence === "present" ? "default" : "secondary"}>
                      {p.presence}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}


// Cession Preview
function CessionPreview({ mouvement }) {
  return (
    <>
      {/* Header */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <ArrowRightLeft className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-900">
              Cession de {mouvement.type_titres || "parts"}
            </h3>
            <p className="text-sm text-amber-700">
              Date: {mouvement.date_cession || "Non detectee"}
              {mouvement.date_effet && mouvement.date_effet !== mouvement.date_cession && 
                ` (Effet: ${mouvement.date_effet})`}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cedants */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <UserMinus className="w-4 h-4 text-red-500" />
              Cedant(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mouvement.cedants?.map((c, idx) => (
              <div key={idx} className="p-2 bg-red-50 rounded mb-2">
                <p className="font-medium text-red-800">
                  {c.denomination || `${c.civilite || ''} ${c.prenom || ''} ${c.nom || ''}`}
                </p>
                <div className="flex justify-between text-sm text-red-600 mt-1">
                  <span>Parts cedees: {c.parts_cedees}</span>
                  <span>Reste: {c.parts_apres} ({c.pourcentage_apres}%)</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Cessionnaires */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-green-500" />
              Cessionnaire(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mouvement.cessionnaires?.map((c, idx) => (
              <div key={idx} className="p-2 bg-green-50 rounded mb-2">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-green-800">
                    {c.denomination || `${c.civilite || ''} ${c.prenom || ''} ${c.nom || ''}`}
                  </p>
                  {c.nouvel_associe && (
                    <Badge className="bg-green-600 text-xs">Nouvel associe</Badge>
                  )}
                </div>
                <div className="flex justify-between text-sm text-green-600 mt-1">
                  <span>Parts acquises: {c.parts_acquises}</span>
                  <span>Total: {c.parts_apres} ({c.pourcentage_apres}%)</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Conditions financieres */}
      {mouvement.conditions_financieres && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Euro className="w-4 h-4 text-blue-500" />
              Conditions financieres
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <DataRow 
              label="Prix total" 
              value={formatCurrency(mouvement.conditions_financieres.prix_total)} 
              highlight 
            />
            <DataRow 
              label="Prix par part" 
              value={formatCurrency(mouvement.conditions_financieres.prix_par_part)} 
            />
            {mouvement.conditions_financieres.modalites_paiement && (
              <DataRow 
                label="Modalites" 
                value={mouvement.conditions_financieres.modalites_paiement} 
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Agrement */}
      {mouvement.agrement?.necessaire && (
        <div className={`p-3 rounded-lg ${mouvement.agrement.obtenu ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'} border`}>
          <div className="flex items-center gap-2">
            {mouvement.agrement.obtenu ? (
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
            <span className={mouvement.agrement.obtenu ? 'text-green-800' : 'text-amber-800'}>
              Agrement {mouvement.agrement.obtenu ? 'obtenu' : 'requis'}
              {mouvement.agrement.organe_agrement && ` (${mouvement.agrement.organe_agrement})`}
              {mouvement.agrement.date_agrement && ` le ${mouvement.agrement.date_agrement}`}
            </span>
          </div>
        </div>
      )}

      {/* New associes list */}
      {mouvement.associes_apres_cession && mouvement.associes_apres_cession.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" />
              Repartition du capital apres cession
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              {mouvement.associes_apres_cession.map((a, idx) => (
                <div key={idx} className="py-2 flex items-center justify-between">
                  <span className="text-sm">
                    {a.denomination || `${a.civilite || ''} ${a.prenom || ''} ${a.nom || ''}`}
                  </span>
                  <div className="text-right">
                    <span className="text-sm font-medium">{a.nombre_parts} parts</span>
                    <span className="text-xs text-indigo-600 ml-2">({a.pourcentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}


// Helper to format currency
function formatCurrency(value) {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }) + ' EUR';
}

// Helper to format any value
function formatValue(value) {
  if (value === null || value === undefined) return "(vide)";
  if (typeof value === "number") return value.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
  return String(value);
}

// Helper component for data rows
function DataRow({ label, value, highlight = false }) {
  return (
    <div className="flex justify-between">
      <span className={highlight ? "font-medium text-gray-700" : "text-gray-500"}>{label}</span>
      <span className={`font-medium ${value ? (highlight ? 'text-indigo-600' : 'text-gray-900') : 'text-gray-300'}`}>
        {value || "—"}
      </span>
    </div>
  );
}

